import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { InsightsApiGateway } from './InsightsGateway';
import type { AvailableRanges, ScorecardSnapshot, ScorecardView } from './InsightsTypes';

/**
 * Drives the insights scorecard page: loads the available ranges (presets +
 * the user's saved custom ranges) and the scorecard for the selected range +
 * view, and lets the user save/delete custom ranges (which the service computes
 * on save).
 */
@injectable()
export class InsightsViewModel extends BaseViewModel {
    snapshot: ScorecardSnapshot | null = null;
    ranges: AvailableRanges = { presets: [], saved: [] };
    rangeKey = '30d';
    view: ScorecardView = 'user';
    loading = false;
    savingRange = false;
    error: string | null = null;

    constructor(private readonly gateway: InsightsApiGateway) {
        super();
    }

    async load(): Promise<void> {
        await this.loadRanges();
        await this.refresh();
    }

    async setRange(rangeKey: string): Promise<void> {
        if (rangeKey === this.rangeKey) return;
        this.rangeKey = rangeKey;
        await this.refresh();
    }

    async setView(view: ScorecardView): Promise<void> {
        if (view === this.view) return;
        this.view = view;
        await this.refresh();
    }

    async saveCustomRange(label: string, startDate: string, endDate: string): Promise<void> {
        this.savingRange = true;
        this.notifyChange();
        try {
            const saved = await this.gateway.saveRange(label, startDate, endDate);
            await this.loadRanges();
            this.rangeKey = `custom:${saved.id}`;
            await this.refresh();
        } finally {
            this.savingRange = false;
            this.notifyChange();
        }
    }

    async deleteRange(id: string): Promise<void> {
        await this.gateway.deleteRange(id);
        if (this.rangeKey === `custom:${id}`) this.rangeKey = '30d';
        await this.loadRanges();
        await this.refresh();
    }

    private async loadRanges(): Promise<void> {
        try {
            this.ranges = await this.gateway.fetchRanges();
        } catch {
            this.ranges = { presets: [], saved: [] };
        }
    }

    private async refresh(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            this.snapshot = await this.gateway.fetchScorecard(this.rangeKey, this.view);
        } catch (cause) {
            this.error = cause instanceof Error ? cause.message : 'Failed to load scorecard';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
